import { STAGE_LABEL, type Kit, dueKey } from '@/domain';
import { strings } from '@/shared/strings';
import { labToday } from '@/shared/format/instant';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import { ReworkIcon } from '@/ui/icons';
import styles from './CaseSummary.module.css';

/**
 * The top of a scanned case: what a technician reads at a glance with the tray
 * in their other hand. Request of 4 October 2026 (second), section 8.
 *
 * The job order number large, URGENT when it is, then status, the department
 * the case is at, who it is assigned to and when it is due, labels over values
 * in one grid. It stays at the top of the screen as the case scrolls under it.
 */
export function CaseSummary({ kit, running }: { kit: Kit; running: boolean }) {
  const status = kit.archived
    ? strings.caseView.archived
    : kit.held
      ? strings.caseView.onHold
      : running
        ? strings.caseView.inProgress
        : strings.caseView.waiting;
  // Red once the date has passed, amber on the day and the day before. The request of 7 October 2026, section 6.
  const due = dueKey(kit.deliveryDate, labToday());
  const finished = kit.stage === 'ready';
  const dueTone =
    due === 'later' || finished ? '' : due === 'overdue' ? styles.late : styles.soon;

  return (
    <header className={styles.summary} data-testid="case-summary">
      <div className={styles.top}>
        <h2 className={styles.jo}>{kit.jobOrderNo}</h2>
        {kit.urgent && <span className={styles.urgent}>{strings.orders.urgent.toUpperCase()}</span>}
        {kit.rework && (
          <span className={styles.rework}>
            <ReworkIcon size={13} /> {strings.caseView.rework}
          </span>
        )}
      </div>
      <dl className={styles.grid}>
        <div>
          <dt>{strings.caseView.status}</dt>
          <dd className={kit.held || kit.archived ? styles.stopped : running ? styles.going : ''}>{status}</dd>
        </div>
        <div>
          <dt>{strings.caseView.department}</dt>
          <dd>{STAGE_LABEL[kit.stage]}</dd>
        </div>
        <div>
          <dt>{strings.caseView.assignedTo}</dt>
          <dd>{kit.assignedToUserName ?? strings.caseView.unassigned}</dd>
        </div>
        <div>
          <dt>{strings.caseView.due}</dt>
          <dd className={dueTone}>
            <DeliveryDate date={kit.deliveryDate} finished={finished} emphasis />
          </dd>
        </div>
      </dl>
    </header>
  );
}
